import { Spacer, Text } from '@oy/ui';
import { Colors } from '@oy/uicore';
import { SystemIcon } from '@oy/uiicon';
import classNames from 'classnames';
import React from 'react';
import Dot from '../Dot/Dot';
import styles from './Step.module.css';

enum StepDirection {
  HORIZONTAL = 'HORIZONTAL',
  VERTICAL = 'VERTICAL',
}

enum StepType {
  NON_LINEAR = 'NON_LINEAR',
  LINEAR = 'LINEAR',
}

enum StepVariant {
  STANDARD = 'STANDARD',
  MINIMAL = 'MINIMAL',
}

export type StepProps = {
  direction: StepDirection;
  active: boolean;
  completed: boolean;
  index: number;
  label: string;
  subLabel?: string;
  stepLabel?: string;
  rightIcon?: React.ReactNode;
  isSubStep?: boolean;
  highlightAfter?: boolean;
  type?: StepType;
  completedDotColor?: Colors;
  variant?: StepVariant;
};

const Step = ({
  direction,
  active,
  completed,
  index,
  label,
  subLabel,
  stepLabel,
  rightIcon,
  isSubStep,
  highlightAfter,
  type,
  completedDotColor,
  variant = StepVariant.STANDARD,
}: StepProps) => {
  const isHorizontalDirection = direction === StepDirection.HORIZONTAL;
  const isHorizontalLinear = type === StepType.LINEAR;
  const isStandardVariant = variant === StepVariant.STANDARD;

  let dotColor = Colors.BLACK40;
  let labelColor = Colors.BLACK100;
  if (active) {
    dotColor = Colors.BLUE80;
  } else if (completed) {
    dotColor = completedDotColor ? completedDotColor : Colors.BLACK100;
  } else if (highlightAfter) {
    labelColor = Colors.BLACK40;
  }

  const maxDotSize = 32;

  const dotSize = isSubStep
    ? 10
    : active && isStandardVariant
    ? maxDotSize
    : 24;

  const indexStyle =
    active && isStandardVariant
      ? Text.Style.HeadingXs
      : Text.Style.BodyMdRegular;

  return (
    <div
      className={classNames(styles.container, {
        [styles.flexHorizontal]: isHorizontalDirection,
        [styles.flexVertical]: !isHorizontalDirection,
        [styles.flexAlign]: isHorizontalLinear,
      })}
    >
      <Spacer
        {...(isHorizontalDirection
          ? { right: Spacer.Size.S }
          : { bottom: Spacer.Size.XS })}
        className={classNames(styles.container, {
          [styles.marginHorizontal]: isHorizontalLinear,
        })}
      >
        <Dot color={dotColor} size={dotSize} maxSize={maxDotSize}>
          {completed ? (
            <SystemIcon color={Colors.WHITE} name="vmark" />
          ) : (
            <Text color={Colors.WHITE} textStyle={indexStyle}>
              {stepLabel ? stepLabel : index}
            </Text>
          )}
        </Dot>
      </Spacer>
      <div
        className={classNames({
          [styles.labelHorizontalContainer]: isHorizontalDirection,
          [styles.textCenter]: !isHorizontalDirection,
          [styles.textMinimal]: !isStandardVariant,
        })}
      >
        <Text color={labelColor} textStyle={Text.Style.BodyMdSemibold}>
          {label}
        </Text>
        {subLabel && (
          <Text color={Colors.BLACK40} textStyle={Text.Style.BodySmRegular}>
            {subLabel}
          </Text>
        )}
      </div>
      {rightIcon && isHorizontalDirection && <>{rightIcon}</>}
    </div>
  );
};

Step.Direction = StepDirection;
Step.Variant = StepVariant;

export default Step;
